import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DrawerModal, Flex, Icon, Label, Popover, Switch, Button, notification } from '../../common';
import tenantTheme from '@theme';
import tenantData from '@data';
import TenantComponents from '@components';
import { getTimeDateString } from '../../../utility/date';
import { TIME_DATE_FORMAT } from '../../../constants/formats';
import { useUpdateAutoRenewMutation } from '../../../apis/listings';
import { useApplyProductModalDataOman } from '../../../hooks/useApplyProductModalDataOman';
import { getListingActions } from '../../../tenant/common/data/products';

/** Unified listings table: Posted / Expiring / AUTO-RENEW in its own column (not under stats). */
export const UnifiedPlatformListingTimeline = (props) => {
  const platformData = props?.[props?.slug] || {};
  const { showPostedAt, postedOn, expiryDate, user } = platformData;
  const hidePostNowBtn = props?.bayut?.hidePostNowBtn ?? platformData?.hidePostNowBtn;
  const showPostNow = !showPostedAt && !hidePostNowBtn;
  const { t } = useTranslation();
  const deductionModalRef = useRef();
  const serviceDataRef = useRef();
  const [updateAutoRenew] = useUpdateAutoRenewMutation();
  const [autoRenewModalVisible, setAutoRenewModalVisible] = useState(false);
  const [autoRenewLoading, setAutoRenewLoading] = useState(false);
  const [autoRenewLocalPatch, setAutoRenewLocalPatch] = useState(null);

  const {
    confirmationLoading,
    selectedPaymentOption,
    paymentOptions,
    action,
    onSuccessQuotaCreditModal,
    getDeduction,
    onChangePaymentOption,
    onCancelModal,
    actionLoading,
  } = useApplyProductModalDataOman(user, deductionModalRef, serviceDataRef, true);

  const propertyId = props?.property_id ?? platformData?.property_id;

  useEffect(() => {
    setAutoRenewLocalPatch(null);
  }, [propertyId]);

  const autoRenewItem =
    autoRenewLocalPatch != null
      ? { ...(props?.auto_renewable_item || {}), ...autoRenewLocalPatch }
      : props?.auto_renewable_item;
  const autoRenewApplied = !!autoRenewItem?.isApplied;

  const handlePostNowClick = () => {
    getDeduction('credit', props, getListingActions('basic-listing', props), 'bayut');
  };

  if (!showPostedAt && !showPostNow) {
    return null;
  }

  const autoRenewModal = (
    <DrawerModal
        title={`${autoRenewApplied ? t('Disable') : t('Enable')} ${t('Auto Renew')}`}
        visible={autoRenewModalVisible}
        onOk={async () => {
          setAutoRenewLoading(true);
          const autoRenewId = autoRenewApplied ? autoRenewItem?.id : null;
          const nextApplied = !autoRenewApplied;
          setAutoRenewLocalPatch({
            isApplied: nextApplied,
            id: nextApplied ? autoRenewItem?.id ?? null : null,
            renewing_on: nextApplied ? autoRenewItem?.renewing_on ?? null : null,
          });
          try {
            const response = await updateAutoRenew({
              autoRenewId,
              autoRenewableId: propertyId,
              autoRenewableType: 'Listing',
              productId: tenantData.getListingActions('basic-listing')?.id,
            });
            if (response?.error) {
              setAutoRenewLocalPatch(null);
              notification.error(response?.error);
              return;
            }
            const payload = response?.data;
            if (payload && typeof payload === 'object') {
              setAutoRenewLocalPatch({
                isApplied: !!payload.isApplied,
                id: payload.id ?? null,
                renewing_on: payload.renewing_on ?? null,
              });
            }
            notification.success(t('Updated Successfully'));
            setAutoRenewModalVisible(false);
          } catch {
            setAutoRenewLocalPatch(null);
            notification.error(t('Something went wrong'));
          } finally {
            setAutoRenewLoading(false);
          }
        }}
        onCancel={() => setAutoRenewModalVisible(false)}
        loading={autoRenewLoading}
        height="auto"
      >
        {!autoRenewApplied
          ? t(
              "By turning the 'Auto-Renew' feature ON. The system will automatically renew the listing when it reaches its expiry, if you have enough credits available",
            )
          : t('Are you sure you want to disable auto renew for this listing')}
    </DrawerModal>
  );

  return (
    <>
      {showPostedAt && autoRenewModal}
      <TenantComponents.QuotaCreditModal
        ref={deductionModalRef}
        title={action?.applicableProduct?.requestTitle}
        action={action}
        okText="Apply Now"
        onSuccess={onSuccessQuotaCreditModal}
        onCancel={onCancelModal}
        loading={confirmationLoading}
        serviceDataRef={serviceDataRef}
        selectedPaymentOption={selectedPaymentOption}
        paymentOptions={paymentOptions}
        onChangePaymentOption={onChangePaymentOption}
        showInsufficientCreditsAlert
        showInfoMessage
        hideDataTable
        unifiedCreditsOnly
      />
      <Flex vertical gap="8px" align="start" className="w-100">
        {showPostedAt && (
          <>
            <Flex gap="4px">
              <div className="text-muted">{t('Posted')}</div>
              <div className="text-default fw-600">{getTimeDateString(postedOn) || '-'}</div>
              <Popover content={<div>{getTimeDateString(postedOn, TIME_DATE_FORMAT)}</div>} action="hover">
                <>
                  <Icon
                    icon="AiOutlineInfoCircle"
                    size="1em"
                    color={tenantTheme['primary-color']}
                    style={{ marginTop: '4px' }}
                  />
                </>
              </Popover>
            </Flex>
            <Flex gap="4px">
              <div className="text-muted">{t('Expiring')}</div>
              <div className="text-default fw-600">{getTimeDateString(expiryDate) || '-'}</div>
            </Flex>
            <Flex gap="6px" align="center" style={{ marginTop: 2 }}>
              <Switch
                switchOnly
                name="auto-renew-unified-timeline"
                size="small"
                value={autoRenewApplied}
                loading={autoRenewLoading}
                onChange={() => setAutoRenewModalVisible(true)}
              />
              <Label htmlFor="auto-renew-unified-timeline" className="fz-10" color={tenantTheme['primary-color']}>
                {t('AUTO-RENEW')}
              </Label>
            </Flex>
          </>
        )}
        {showPostNow && (
          <Button
            type="primary"
            icon="MdOutlineAddLocationAlt"
            style={{ maxWidth: '175px' }}
            onClick={handlePostNowClick}
            block
            loading={actionLoading}
          >
            {t('Post Now')}
          </Button>
        )}
      </Flex>
    </>
  );
};
